import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import apiClient from "../services/api";
import { Program } from "../types/program";
// Cambridge Research Programmes components
import CambridgeHero from "../components/cambridge/Hero";
import ProgramFeaturesCambridge from "../components/cambridge/ProgramFeatures";
import KeyHighlights from "../components/cambridge/KeyHighlights";
import ProgramDetails from "../components/cambridge/ProgramDetails";
import ProgramSelection from "../components/cambridge/ProgramSelection";
import WhyChoose from "../components/cambridge/WhyChoose";
import FAQ from "../components/cambridge/FAQ";
import CTA from "../components/cambridge/CTA";
// Shared components
import HeroSection from "../components/shared/HeroSection";
import ProgramFeatures from "../components/shared/ProgramFeatures";
import SmartSidebar from "../components/shared/SmartSidebar";

const ProgramDetail: React.FC = () => {
  const { id, parent, child } = useParams<{ id?: string; parent?: string; child?: string }>();
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine which slug to use based on URL pattern
  const programSlug = child || id;

  useEffect(() => {
    const fetchProgram = async () => {
      if (!programSlug) return;

      try {
        setLoading(true);
        setError(null);

        // Redirect old harvard-negotiation-workshop slug to new ivy-league-negotiation-workshop
        if (programSlug === "harvard-negotiation-workshop") {
          window.location.href = "/programs/ivy-league-negotiation-workshop";
          return;
        }

        // Map frontend slug to backend slug
        const slugMapping: { [key: string]: string } = {
          "ivy-league-negotiation-workshop": "harvard-negotiation-workshop",
        };
        const backendSlug = slugMapping[programSlug] || programSlug;

        // Try to fetch the program by slug
        const response = await apiClient.getProgram(backendSlug);

        if (response.success) {
          const programData = response.data.program || response.data;

          // Special handling for specific programs
          if (
            programData.slug === "premier-workshop" ||
            programData.category === "workshop"
          ) {
            window.location.href = `/workshop-booking/${programData.slug}`;
            return;
          }

          // Redirect NASA programs to dedicated NASA page
          if (
            programData.slug === "nasa-space-training" ||
            programData.category === "NASA Partnership"
          ) {
            window.location.href = "/nasa-programs";
            return;
          }

          // Update slug to frontend slug if it's harvard-negotiation-workshop
          if (programData.slug === "harvard-negotiation-workshop") {
            programData.slug = "ivy-league-negotiation-workshop";
          }

          setProgram(programData);

          // Handle hash-based scrolling (e.g., #program-selection)
          const hash = window.location.hash;
          if (hash) {
            setTimeout(() => {
              const element = document.getElementById(hash.substring(1));
              if (element) {
                const headerOffset = 120;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition =
                  elementPosition + window.pageYOffset - headerOffset;
                window.scrollTo({
                  top: offsetPosition,
                  behavior: "smooth",
                });
              }
            }, 500);
          }
        } else {
          setError("Program not found");
        }
      } catch (err: any) {
        console.error("Error fetching program:", err);
        setError("Failed to load program details");
      } finally {
        setLoading(false);
      }
    };

    fetchProgram();
  }, [programSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading program details...</p>
        </div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Program Not Found
            </h1>
            <p className="text-gray-600 mb-8">
              {error || "The program you are looking for does not exist."}
            </p>
            <Link
              to="/programs"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Programs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Check if this is research-programmes-at-cambridge for special layout
  const isCambridgeResearch =
    (program as any)?.slug === "research-programmes-at-cambridge";

  // Check if this program has children (is a parent program)
  const hasChildren = program.children && program.children.length > 0;
  
  // Check if this is a sub-program (has a parent)
  const isSubProgram = program.parent_id && program.parent;

  return (
    <div className="min-h-screen bg-white">
      {isCambridgeResearch ? (
        <>
          {/* Cambridge Research Programmes Layout */}
          <CambridgeHero program={program} />
          <KeyHighlights />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-1">
              <div className="w-full">
                <ProgramFeaturesCambridge program={program} />
                <ProgramDetails />
                <ProgramSelection />
                <WhyChoose />
                <FAQ />
              </div>
            </div>
          </div>
          <CTA />
        </>
      ) : (
        <>
          {/* General Programs Layout */}
          <HeroSection program={program} />
          
          {/* Show parent program link if this is a sub-program */}
          {isSubProgram && program.parent && (
            <div className="bg-blue-50 border-b border-blue-100">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center gap-2 text-sm">
                  <Link
                    to={`/programs/${program.parent.slug}`}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    ← Back to {program.parent.title}
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <ProgramFeatures program={program} />
                
                {/* Show child programs if this is a parent program */}
                {hasChildren && program.children && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mt-12"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">
                      Available Programs
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {program.children.map((child) => (
                        <Link
                          key={child.id}
                          to={`/programs/${program.slug}/${child.slug}`}
                          className="block p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-lg transition-all duration-300"
                        >
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            {child.title}
                          </h3>
                          <span className="inline-flex items-center text-blue-600 font-medium">
                            View Details →
                          </span>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
              <div className="lg:col-span-1">
                <SmartSidebar program={program} programId={programSlug} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProgramDetail;
